import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { fetchOriginalPaymentPlan } from "../gestionCarteraApi";
import {
  defaultOriginalPlanColumns,
  formatDate,
  formatMoney,
  ORIGINAL_PLAN_COLUMNS,
  sumColumn,
  visibleOriginalPlanColumns,
} from "../gestionCarteraFormat";
import { cellSx, COLORS, headCellSx, ModalShell, SecondaryButton } from "../gestionCarteraUi";

const TOGGLEABLE_COLUMNS = ORIGINAL_PLAN_COLUMNS.filter((column) => !column.fixed);

const formatCell = (column, row, quotasCount) => {
  const value = row[column.key];
  if (column.type === "quota") return `${String(value).padStart(2, "0")} / ${quotasCount}`;
  if (column.type === "date") return formatDate(value);
  return formatMoney(value);
};

function ColumnsMenu({ selected, onToggle, onReset }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <SecondaryButton
        startIcon={<ViewColumnIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
      >
        Columnas
      </SecondaryButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {TOGGLEABLE_COLUMNS.map((column) => (
          <MenuItem key={column.key} dense onClick={() => onToggle(column.key)}>
            <FormControlLabel
              control={<Checkbox size="small" checked={selected.includes(column.key)} />}
              label={column.label}
              sx={{ pointerEvents: "none", m: 0 }}
            />
          </MenuItem>
        ))}
        <MenuItem dense onClick={onReset} sx={{ color: COLORS.primary, fontWeight: 500 }}>
          Restablecer columnas
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Plan de pagos original (solo lectura): lo que se estimaba en la originación que el cliente
 * pagara en cada cuota y el proyectado de cada componente. Columnas ocultables.
 */
export default function PlanPagosOriginalModal({ open, credit, title, onClose }) {
  const [resource, setResource] = useState({ status: "loading", data: null });
  const [selected, setSelected] = useState(defaultOriginalPlanColumns);

  useEffect(() => {
    if (!open || !credit) return;
    setResource({ status: "loading", data: null });
    fetchOriginalPaymentPlan(credit.uuid)
      .then((data) => setResource({ status: "ready", data }))
      .catch(() => setResource({ status: "error", data: null }));
  }, [open, credit]);

  const toggleColumn = (key) =>
    setSelected((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

  const rows = resource.data?.quotas || [];
  const columns = visibleOriginalPlanColumns(selected);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Plan de pagos original"
      subtitle={`${title} · Lo estimado en la originación, por cuota y componente`}
      maxWidth="xl"
      actions={<SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ColumnsMenu selected={selected} onToggle={toggleColumn} onReset={() => setSelected(defaultOriginalPlanColumns())} />
      </Box>

      {resource.status === "loading" && <Skeleton variant="rounded" height={360} />}
      {resource.status === "error" && <Alert severity="error">No pudimos cargar el plan de pagos original.</Alert>}
      {resource.status === "ready" && rows.length === 0 && (
        <Alert severity="info">Este crédito no tiene un plan de pagos original registrado.</Alert>
      )}
      {resource.status === "ready" && rows.length > 0 && (
        <TableContainer sx={{ maxHeight: { xs: "none", sm: "60vh" }, border: `1px solid ${COLORS.border}`, borderRadius: "12px" }}>
          <Table stickyHeader size="small" aria-label="Plan de pagos original">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ ...headCellSx, backgroundColor: "#fff" }}>{column.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.quotaNumber} hover>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={cellSx}>{formatCell(column, row, rows.length)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ "& td": { ...cellSx, fontWeight: 500, fontSize: 14, backgroundColor: COLORS.rowHover, position: "sticky", bottom: 0 } }}>
                {columns.map((column, index) => (
                  <TableCell key={column.key}>
                    {index === 0 ? "Total del plan" : column.total ? formatMoney(sumColumn(rows, column.key)) : ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </ModalShell>
  );
}
